import {
  Box,
  BoxProps,
  Button,
  Chip,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import {
  type AssistantModalTheme,
  modalThemeTokens,
  buildModalCssVars,
} from '../AssistantEditorModals/theme';

export const RagStorageDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== '$theme',
})<{ $theme?: AssistantModalTheme }>(({ $theme = 'light' }) => {
  const tokens = modalThemeTokens[$theme];
  const cssVars = buildModalCssVars(tokens);
  return {
    ...(cssVars as any),
    ...($theme === 'dark' && {
      '--am-paper-bg':
        'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
      '--am-header-bg': 'rgba(15, 17, 23, 0.75)',
      '--am-body-bg':
        'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
      // Reference input treatment (Editor Mode tester input) — scoped to this
      // dialog's own vars rather than the shared --am-input-bg/--am-border-mid
      // tokens, since those are reused for unrelated things (disabled button
      // fill, dashed borders, radio cards) that shouldn't get this translucent
      // glass look.
      '--am-textarea-bg': 'rgba(0, 0, 0, 0.32)',
      '--am-textarea-border': 'rgba(255, 255, 255, 0.055)',
      '--am-textarea-focus-border': 'rgba(110, 108, 246, 0.5)',
      '--am-textarea-radius': '10px',
      '--am-textarea-placeholder': '#71757f',
    } as any),
    '& .MuiBackdrop-root': { backgroundColor: tokens.backdrop },
    '& .MuiDialog-paper': {
      width: '60%',
      borderRadius: '16px',
      padding: 0,
      boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.12)',
      background: tokens.paperBg,
      ...($theme === 'dark' && {
        background:
          'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
      }),
      // MUI form element overrides so TextField/Toggle respect the theme
      '& .MuiInputBase-root': {
        color: 'var(--am-text-primary)',
        backgroundColor: 'var(--am-textarea-bg, var(--am-input-bg))',
      },
      '& .MuiInputBase-root.Mui-disabled': {
        WebkitTextFillColor: 'var(--am-text-disabled)',
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 'var(--am-textarea-radius, 4px)',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--am-textarea-border, var(--am-border-mid))',
      },
      '& .MuiInputBase-input::placeholder': {
        color: 'var(--am-textarea-placeholder, var(--am-text-secondary))',
        opacity: 1,
      },
      '& .MuiInputLabel-root': {
        color: 'var(--am-text-secondary)',
      },
      '& .MuiToggleButton-root': {
        color: 'var(--am-text-secondary)',
        borderColor: 'var(--am-border-mid)',
      },
      '& .MuiToggleButton-root.Mui-selected': {
        backgroundColor: 'var(--am-accent-surface)',
        color: 'var(--am-text-primary)',
      },
      ...($theme === 'dark' && {
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--am-textarea-focus-border, var(--am-focus-border-color))',
        },
      } as any),
    },
    '& .MuiBox-root': { maxWidth: '100%' },
  };
});

export const ModalContent = styled(Box)(({ theme }) => ({
  background: 'var(--am-paper-bg)',
  borderRadius: '16px',
  width: '100%',
  position: 'relative',
  boxShadow: theme.shadows[24],
}));

export const RagStorageModalHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
});

export const RagStorageModalBody = styled(Box)({
  padding: '16px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const RagStorageModalActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  marginTop: theme.spacing(1),
}));

export const DialogHeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const DialogStorageIcon = styled(StorageOutlinedIcon)({
  color: 'var(--am-icon-accent)',
  fontSize: 22,
});

export const HintIcon = styled(LightbulbOutlinedIcon)({
  fontSize: 18,
  color: 'var(--am-text-secondary)',
  marginTop: 1,
  flexShrink: 0,
});

export const DialogModalTitle = styled(Typography)({
  fontWeight: 400,
  fontSize: '18px',
  color: 'var(--am-text-primary)',
});

export const ErrorCaption = styled(Typography)({
  marginTop: 4,
}) as typeof Typography;

export const DialogCancelButton = styled(Button)({
  height: '48px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'none',
  color: 'var(--am-text-primary)',
  backgroundColor: 'transparent',
  border: '2px solid var(--am-cancel-border)',
  padding: '12px 24px',
  minWidth: '160px',
  transition: 'all 0.2s ease',

  '&:hover': {
    backgroundColor: 'var(--am-cancel-hover-bg)',
    border: '2px solid var(--am-cancel-hover-border)',
  },
  '&:disabled': { opacity: 0.5 },
});

export const DialogPrimaryButton = styled(Button)({
  height: '48px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'none',
  color: 'var(--am-primary-btn-color)',
  backgroundColor: 'var(--am-primary-btn-bg)',
  padding: '12px 24px',
  minWidth: '180px',
  border: 'none',
  transition: 'all 0.2s ease',

  '&:hover': { backgroundColor: 'var(--am-primary-btn-hover-bg)' },
  '&:disabled': {
    backgroundColor: 'var(--am-border-mid)',
    color: 'var(--am-text-secondary)',
  },
});

export const CloseButton = styled(IconButton)({
  color: 'var(--am-close-color)',
  padding: '4px',

  '&:hover': {
    backgroundColor: 'var(--am-surface-2)',
    color: 'var(--am-text-primary)',
  },
});

export const FieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--am-text-secondary)',
  marginBottom: '4px',
});

export const FormField = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const CommonTagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
  fontWeight: 500,

  '& .MuiChip-deleteIcon': {
    color: alpha(theme.palette.primary.main, 0.6),

    '&:hover': { color: theme.palette.primary.main },
  },
}));

export const GeneralTagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.success.main, 0.12),
  color: theme.palette.success.dark,
  fontWeight: 500,

  '& .MuiChip-deleteIcon': {
    color: alpha(theme.palette.success.main, 0.6),

    '&:hover': { color: theme.palette.success.dark },
  },
}));

export const TagHintBox = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  marginTop: 8,
  padding: '10px 12px',
  borderRadius: '4px',
  backgroundColor: 'var(--am-accent-surface)',
});

export const InlineCode: typeof Box = styled(Box)(({ theme }) => ({
  paddingLeft: '0.6em',
  paddingRight: '0.6em',
  paddingTop: '0.15em',
  paddingBottom: '0.15em',
  borderRadius: '0.5em',
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  color: theme.palette.primary.main,
  fontSize: '0.8em',
  fontFamily: 'monospace',
})) as typeof Box;

interface TextDataAreaProps extends BoxProps<'textarea'> {
  hasError?: boolean;
  isLoading?: boolean;
}

export const TextDataArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'isLoading',
})<TextDataAreaProps>(({ theme, hasError, isLoading }) => ({
  width: '100%',
  resize: 'vertical',
  fontFamily: 'inherit',
  fontSize: '14px',
  padding: '10px 12px',
  borderRadius: `var(--am-textarea-radius, ${theme.shape.borderRadius}px)`,
  border: `1px solid ${
    hasError ? theme.palette.error.main : 'var(--am-textarea-border, var(--am-border-mid))'
  }`,
  backgroundColor: isLoading
    ? 'var(--am-surface)'
    : 'var(--am-textarea-bg, var(--am-input-bg))',
  color: 'var(--am-text-primary)',

  '&:focus': {
    outline: 'none',
    borderColor: 'var(--am-textarea-focus-border, var(--am-focus-border-color))',
  },
  '&:disabled': {
    color: 'var(--am-text-secondary)',
    cursor: 'not-allowed',
  },
  '&::placeholder': {
    color: 'var(--am-textarea-placeholder, var(--am-text-secondary))',
    opacity: 1,
  },
}));
